import { useState, type FormEvent } from "react";
import { ApiError, tokensSchema } from "@nahal/api-client";
import { Button, Card, ErrorState, TextField } from "@nahal/ui";
import { http } from "../../lib/api";
import { useSession } from "../../lib/session";

/**
 * The console owns sign-in. The marketing site is the front door and hands the
 * visitor here rather than holding a session of its own -- one owner, no drift.
 */
export function SignInScreen() {
  const setTokens = useSession((state) => state.setTokens);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const tokens = await http.request("/api/v1/identity/login", {
        method: "POST",
        body: { email, password },
        schema: tokensSchema,
      });
      setTokens(tokens.accessToken, tokens.refreshToken);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col justify-center bg-[var(--surface-page)] px-4 py-10">
      <div className="mx-auto w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <span className="grid size-8 place-items-center rounded-[var(--radius-control)] bg-[var(--accent-solid)] text-sm font-bold text-white">
            N
          </span>
          <span className="text-base font-semibold tracking-tight text-[var(--text-primary)]">
            Nahal Labs Console
          </span>
        </div>

        <Card className="p-5">
          <h1 className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">Sign in</h1>
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
            One account for Nahalo, Nahal Desk and Nahal SMS.
          </p>

          {error && (
            <div className="mt-4">
              <ErrorState message={error} />
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-4">
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <Button type="submit" variant="solid" size="lg" className="w-full" disabled={busy}>
              {busy ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </Card>

        <p className="mt-4 text-center text-sm text-[var(--text-secondary)]">
          Need an account?{" "}
          <a className="font-medium text-[var(--text-brand)]" href="https://nahallabs.com/contact">
            Talk to us
          </a>
        </p>
      </div>
    </div>
  );
}
